import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const DIACRITICS_REGEX = new RegExp(
  `[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`,
  "g",
);

/** Lowercase + strip diacritics so "mexico" matches "México". */
function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(DIACRITICS_REGEX, "")
    .toLowerCase()
    .trim();
}

export class InputTagAutocomplete implements Plugin {
  private readonly input: HTMLInputElement | null;
  private readonly tagsContainer: HTMLElement | null;
  private readonly suggestionsList: HTMLElement | null;

  private readonly options: string[];
  private readonly minChars: number;

  private visibleItems: string[] = [];
  private activeIndex = -1;

  private readonly inputHandler: () => void;
  private readonly keydownHandler: (e: KeyboardEvent) => void;
  private readonly documentClickHandler: (e: MouseEvent) => void;

  constructor(private readonly element: HTMLElement) {
    this.input = this.element.querySelector(".core-inputTag__input");
    this.tagsContainer = this.element.querySelector(".core-inputTag__tags");
    this.suggestionsList = this.element.querySelector(
      ".core-inputTag__suggestions",
    );

    this.options = this.parseOptions();
    this.minChars = parseInt(this.element.dataset.minChars || "1", 10);

    this.inputHandler = () => this.handleInput();
    this.keydownHandler = (e: KeyboardEvent) => this.handleKeydown(e);
    this.documentClickHandler = (e: MouseEvent) => this.handleDocumentClick(e);
  }

  init(): void {
    if (!this.input || !this.suggestionsList) return;

    this.input.addEventListener("input", this.inputHandler);
    this.input.addEventListener("keydown", this.keydownHandler);
    document.addEventListener("click", this.documentClickHandler);
  }

  private parseOptions(): string[] {
    try {
      const raw = this.element.dataset.options || "[]";
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((v) => typeof v === "string")
        : [];
    } catch {
      return [];
    }
  }

  private getSelectedValues(): string[] {
    if (!this.tagsContainer) return [];
    return Array.from(
      this.tagsContainer.querySelectorAll<HTMLElement>(".core-tagInput"),
    ).map((tag) => tag.dataset.value || "");
  }

  private handleInput(): void {
    const rawTerm = this.input?.value.trim() || "";

    if (rawTerm.length < this.minChars) {
      this.hideList();
      return;
    }

    const term = normalize(rawTerm);
    const selected = this.getSelectedValues();
    const filtered = this.options.filter(
      (option) =>
        !selected.includes(option) && normalize(option).includes(term),
    );

    this.renderSuggestions(filtered);
  }

  private renderSuggestions(items: string[]): void {
    if (!this.suggestionsList) return;

    this.visibleItems = items;
    this.activeIndex = -1;
    this.suggestionsList.innerHTML = "";

    if (items.length === 0) {
      this.hideList();
      return;
    }

    items.forEach((item) => {
      const li = document.createElement("li");
      li.className = "core-dropdown__item";
      li.setAttribute("role", "option");
      li.dataset.value = item;

      li.innerHTML = `<div class="core-dropdown__content core-dropdown__content--state-default core-dropdown__content--variant-default"><div class="core-dropdown__content__textWrap"><span class="core-dropdown__content__label">${item}</span></div></div>`;

      li.addEventListener("mousedown", (e) => {
        e.preventDefault();
        this.select(item);
      });

      this.suggestionsList!.appendChild(li);
    });

    this.showList();
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (this.suggestionsList?.hasAttribute("hidden")) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.moveActive(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.moveActive(-1);
    } else if (event.key === "Enter") {
      if (this.activeIndex >= 0) {
        event.preventDefault();
        this.select(this.visibleItems[this.activeIndex]);
      } else if (this.visibleItems.length === 1) {
        event.preventDefault();
        this.select(this.visibleItems[0]);
      }
    } else if (event.key === "Escape") {
      this.hideList();
    }
  }

  private moveActive(delta: number): void {
    if (this.visibleItems.length === 0) return;

    this.activeIndex =
      (this.activeIndex + delta + this.visibleItems.length) %
      this.visibleItems.length;

    const items = this.suggestionsList?.querySelectorAll(
      ".core-dropdown__item",
    );
    items?.forEach((item, index) => {
      item.classList.toggle(
        "core-dropdown__item--state-selected",
        index === this.activeIndex,
      );
    });

    items?.[this.activeIndex]?.scrollIntoView({ block: "nearest" });
  }

  private select(value: string): void {
    this.element.dispatchEvent(
      new CustomEvent("coreInputTag:select", {
        bubbles: true,
        detail: { value },
      }),
    );
    this.hideList();
    this.input?.focus();
  }

  private handleDocumentClick(event: MouseEvent): void {
    if (!this.element.contains(event.target as Node)) {
      this.hideList();
    }
  }

  private showList(): void {
    this.suggestionsList?.removeAttribute("hidden");
    this.input?.setAttribute("aria-expanded", "true");
  }

  private hideList(): void {
    this.suggestionsList?.setAttribute("hidden", "");
    this.input?.setAttribute("aria-expanded", "false");
    this.activeIndex = -1;
  }

  destroy(): void {
    this.input?.removeEventListener("input", this.inputHandler);
    this.input?.removeEventListener("keydown", this.keydownHandler);
    document.removeEventListener("click", this.documentClickHandler);
  }
}

PluginRegistry.register("inputTagAutocomplete", InputTagAutocomplete);
